import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/ui/icon';
import { daysUntilDate, formatDateOnly } from '@/lib/date';
import { cn } from '@/lib/utils';
import { DeliveryBadge, SALE_STATUSES, STATUS_DOT_COLOR, STATUS_LABELS } from './status';
import { needsTransitionModal, StatusTransitionModal } from './status-transition-modal';
import { ViewSaleModal } from './view-sale-modal';

interface KanbanBoardProps {
  sales: any[];
  onStatusChange: (id: string, status: string, extra?: Record<string, string>) => void;
}

function urgency(sale: any): 'overdue' | 'soon' | 'normal' {
  if (sale.status === 'delivered' || sale.status === 'canceled') return 'normal';
  const days = daysUntilDate(sale.deliveryDate);
  if (days < 0) return 'overdue';
  if (days <= 3) return 'soon';
  return 'normal';
}

const URGENCY_BORDER: Record<ReturnType<typeof urgency>, string> = {
  overdue: 'border-l-destructive',
  soon: 'border-l-warning',
  normal: 'border-l-transparent',
};

const PRIORITY_BY_URGENCY: Record<
  ReturnType<typeof urgency>,
  { label: string; variant: 'destructive' | 'warning' | 'secondary' }
> = {
  overdue: { label: 'URGENTE', variant: 'destructive' },
  soon: { label: 'ALTA', variant: 'warning' },
  normal: { label: 'MÉDIA', variant: 'secondary' },
};

/** Board de status arrastável — solta num status que precisa de dado extra e abre modal antes de aplicar. */
export function KanbanBoard({ sales, onStatusChange }: KanbanBoardProps) {
  const [pendingDrop, setPendingDrop] = useState<{ id: string; status: string } | null>(
    null,
  );
  const [viewingId, setViewingId] = useState<string | null>(null);

  // distance mínima antes de virar drag — sem isso, um clique simples pra abrir
  // o modal de visualização é interpretado como início de arraste.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const grouped = SALE_STATUSES.reduce<Record<string, any[]>>((acc, status) => {
    acc[status] = sales.filter((sale) => sale.status === status);
    return acc;
  }, {});

  const handleDragEnd = (event: DragEndEvent) => {
    const saleId = event.active.id as string;
    const newStatus = event.over?.id as string | undefined;

    if (!newStatus) return;

    const sale = sales.find((s) => s.id === saleId);
    if (!sale || sale.status === newStatus) return;

    if (needsTransitionModal(newStatus)) {
      setPendingDrop({ id: saleId, status: newStatus });
    } else {
      onStatusChange(saleId, newStatus);
    }
  };

  return (
    <>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4 items-start">
          {SALE_STATUSES.map((status, index) => (
            <KanbanColumn
              key={status}
              status={status}
              sales={grouped[status] ?? []}
              onView={setViewingId}
              delay={index * 80}
            />
          ))}
        </div>
      </DndContext>

      <StatusTransitionModal
        status={pendingDrop?.status ?? null}
        sale={pendingDrop ? (sales.find((s) => s.id === pendingDrop.id) ?? null) : null}
        open={!!pendingDrop}
        onOpenChange={(open) => !open && setPendingDrop(null)}
        onConfirm={(extra) => {
          if (pendingDrop) onStatusChange(pendingDrop.id, pendingDrop.status, extra);
          setPendingDrop(null);
        }}
      />

      <ViewSaleModal saleId={viewingId} onOpenChange={() => setViewingId(null)} />
    </>
  );
}

function KanbanColumn({
  status,
  sales,
  onView,
  delay,
}: {
  status: string;
  sales: any[];
  onView: (id: string) => void;
  delay: number;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const color = STATUS_DOT_COLOR[status] ?? 'var(--faint)';
  const total = sales.reduce((sum, s) => sum + (s.total ?? 0), 0);

  return (
    <div
      ref={setNodeRef}
      style={{ animationDelay: `${delay}ms`, borderBottomColor: color }}
      className={cn(
        'w-[268px] shrink-0 rounded-[14px] border border-transparent transition-colors animate-slide-in',
        isOver && 'ring-2 ring-primary bg-primary-soft/30',
      )}
    >
      <div className="px-2 pb-2 border-b-2" style={{ borderBottomColor: color }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 min-w-0">
            <span
              className="size-1.5 rounded-full shrink-0"
              style={{ backgroundColor: color }}
            />
            <span className="text-sm font-semibold truncate">
              {STATUS_LABELS[status]}
            </span>
          </div>
          <Badge variant="secondary" className="font-mono">
            {sales.length}
          </Badge>
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-faint mt-1">
          {total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
        </div>
      </div>

      <div className="p-2 space-y-2">
        {sales.length === 0 ? (
          <div className="flex h-[104px] flex-col items-center justify-center gap-1.5 rounded-[10px] border border-dashed border-border text-faint">
            <Icon name="check" className="size-4" />
            <span className="text-[10px] font-bold uppercase tracking-[0.08em]">
              Sem pedidos
            </span>
          </div>
        ) : (
          sales.map((sale) => <KanbanCard key={sale.id} sale={sale} onView={onView} />)
        )}
      </div>
    </div>
  );
}

function KanbanCard({ sale, onView }: { sale: any; onView: (id: string) => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: sale.id,
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  const level = urgency(sale);
  const priority = PRIORITY_BY_URGENCY[level];

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={() => onView(sale.id)}
      className={cn(
        'bg-card border border-border rounded-[10px] px-2.5 py-2 cursor-grab active:cursor-grabbing space-y-1.5 border-l-[3px] transition-[transform,box-shadow,border-color] hover:border-border-strong hover:-translate-y-[2px] hover:shadow-[var(--shadow-float)]',
        URGENCY_BORDER[level],
        isDragging && 'opacity-70 z-10 shadow-lg',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-mono text-muted-foreground">
          {sale.saleNumber ?? sale.id.slice(0, 8)}
        </span>
        <Badge variant={priority.variant}>{priority.label}</Badge>
      </div>

      <div className="text-[13.5px] font-semibold truncate">
        {sale.customer?.name ?? '—'}
      </div>

      <div className="border-t border-border pt-1.5 flex items-center justify-between">
        <DeliveryBadge deliveryDate={sale.deliveryDate} status={sale.status} />
        <strong className="text-xs font-mono">
          {(sale.total ?? 0).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          })}
        </strong>
      </div>

      {!['delivered', 'canceled'].includes(sale.status) && (
        <div className="text-[11px] text-muted-foreground flex items-center justify-end">
          <span>Entrega em {formatDateOnly(sale.deliveryDate)}</span>
        </div>
      )}
    </div>
  );
}
